import { Injectable } from '@angular/core';
import { Subject, Observable, of } from 'rxjs';
import { Location } from '@angular/common';
import {
  map,
  shareReplay,
  tap,
  distinctUntilChanged,
  catchError
} from 'rxjs/operators';
import * as decode from 'jwt-decode';
import { environment } from 'environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';

// Offset in seconds applied to the expiry time
const EXPIRY_TIME_OFFSET = 30;

@Injectable({
  providedIn: 'root'
})
export class TokenService {
  public adminToken: string = null;
  private initialContext = null;
  private _context: string | null = null;
  private _token: string = null;
  private _tokenChanged$: Subject<string> = new Subject<string>();
  private refreshingToken$: Observable<string> | null = null;
  private tokenExpireTime = 0;
  private application = 'manager';

  constructor(private location: Location, private http: HttpClient) {
    // can be used by iframed applications to request a token
    (window as any).getLightspeedToken = (
      callback?: (token: string, context: string) => void
    ) => {
      if (callback) {
        if (environment.enableSSO) {
          return this.getToken$().subscribe(token =>
            callback(token, this.context)
          );
        } else {
          return callback(sessionStorage.getItem('apitoken'), null);
        }
      }

      if (environment.enableSSO) {
        return this.getToken$()
          .pipe(map(token => ({ token, context: this.context })))
          .toPromise();
      }
      return Promise.resolve({
        token: sessionStorage.getItem('apitoken'),
        context: null
      });
    };
  }

  public getToken$(prefix = true) {
    const bearer = environment.enableSSO && prefix ? 'Bearer ' : '';
    if (this.refreshingToken$) {
      return this.refreshingToken$;
    } else if (
      environment.enableSSO &&
      this.tokenExpireTime < new Date().getTime()
    ) {
      this.refreshingToken$ = this.refreshToken(this.application).pipe(
        map(token => {
          this._token = token;
          this.refreshingToken$ = null;
          return `${bearer}${this._token}`;
        }),
        distinctUntilChanged()
      );
      return this.refreshingToken$;
    }
    return of(`${bearer}${this._token}`).pipe(distinctUntilChanged());
  }

  public set token(token: string) {
    if (token !== this._token) {
      this._token = token;
      try {
        const { exp } = decode(token);
        this.tokenExpireTime = (exp - EXPIRY_TIME_OFFSET) * 1000;
      } catch (err) {
        this.tokenExpireTime = 0;
      }
      this.tokenChanged$.next(token);
    }

    if (token) {
      // if no admin token then set an admin token (for establishment switch)
      if (!this.adminToken) {
        this.adminToken = token;
      }
      // kind of logout: combined report on establishment childrens are forbidden (to refact)
      if (!/combinedreports/.test(this.location.path())) {
        sessionStorage.setItem('apitoken', token);
      }
    } else {
      // ??
      this.adminToken = null;
      sessionStorage.removeItem('apitoken');
    }
  }

  public get tokenChanged$(): Subject<string> {
    return this._tokenChanged$;
  }

  public get token(): string {
    return this._token;
  }

  public hasToken(): boolean {
    return !!this._token;
  }

  public get context(): string {
    return this._context;
  }

  public set context(context: string) {
    this._context = context || this.initialContext;
  }

  public setInitialContext(context: string) {
    this.initialContext = context;
    this.context = context;
  }

  public initPartnerToken(): Observable<string> {
    return this.refreshToken('partner').pipe(
      tap(() => {
        this.application = 'partner';
      })
    );
  }

  public getApplication() {
    return this.application;
  }

  private refreshToken(application: string): Observable<string> {
    return this.http
      .post<{ access_token: string; expires_in: number }>(
        `${environment.authService}/${application}/refresh`,
        {},
        {
          headers: new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded'
          }),
          withCredentials: true
        }
      )
      .pipe(
        map(resp => {
          this.tokenExpireTime =
            new Date().getTime() +
            (resp.expires_in - EXPIRY_TIME_OFFSET) * 1000;
          return resp.access_token;
        }),
        catchError(err => {
          console.error('REDIRECTING TO AUTH0>>>>>', err);
          window.location.assign(
            `${location.origin.replace('-web', '')}${environment.authService}${
              window.location.hostname &&
              window.location.hostname === 'localhost'
                ? '?local=true'
                : ''
            }`
          );
          return '';
        }),
        shareReplay(1)
      );
  }
}
